import { useLayoutEffect, useRef } from 'react';

const GLIDE_MS = 600;
const GLIDE_ATTR = 'data-glide-key';

type IGlidePositions = Map<string, { left: number; top: number } | null>;

const activeGlides = new WeakMap<HTMLElement, () => void>();

/** A chip's glide key (the selector only matches chips that carry one). */
const glideKeyOf = (element: HTMLElement) => String(element.dataset.glideKey);

/** Cancels an in-flight glide: drops its listener and clears its inline styles. */
function cancelGlide(element: HTMLElement): void {
  activeGlides.get(element)?.();
}

/**
 * Cancels any in-flight glide, then reads every glide key's layout rect.
 * Rects are relative to `container`, not the viewport: the container scrolls
 * with its chips, so a page scroll between two frames is not read as a move.
 * A key that occurs more than once maps to null (ambiguous). `visible` holds the
 * pre-cancel rect of chips that were mid-glide, so a new glide can continue
 * from where the chip visibly is.
 */
function readPositions(container: HTMLElement) {
  const origin = container.getBoundingClientRect();
  const rectOf = (element: HTMLElement) => {
    const { left, top } = element.getBoundingClientRect();
    return { left: left - origin.left, top: top - origin.top };
  };
  const positions: IGlidePositions = new Map();
  const visible: IGlidePositions = new Map();
  const elements = new Map<string, HTMLElement>();
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(`[${GLIDE_ATTR}]`));
  nodes.forEach((element) => {
    if (activeGlides.has(element)) visible.set(glideKeyOf(element), rectOf(element));
  });
  nodes.forEach(cancelGlide);
  nodes.forEach((element) => {
    const key = glideKeyOf(element);
    if (positions.has(key)) {
      positions.set(key, null);
      return;
    }
    positions.set(key, rectOf(element));
    elements.set(key, element);
  });
  return { positions, visible, elements };
}

interface IGlide {
  element: HTMLElement;
  dx: number;
  dy: number;
}

/**
 * Plays every glide with one forced reflow: all chips jump back to their old
 * position first (writes), one layout read commits those starts, then all
 * transitions start (writes) — never a read between two chips' writes.
 */
function playGlides(glides: IGlide[]): void {
  const [first] = glides;
  if (!first) return;
  for (const { element, dx, dy } of glides) {
    element.style.transition = 'none';
    element.style.transform = `translate(${dx}px, ${dy}px)`;
  }
  void first.element.offsetWidth; // force one reflow so every start position is committed
  for (const { element } of glides) {
    const onEnd = (event: TransitionEvent) => {
      if (event.target === element && event.propertyName === 'transform') cleanup();
    };
    const cleanup = () => {
      element.removeEventListener('transitionend', onEnd);
      activeGlides.delete(element);
      element.style.transition = '';
      element.style.transform = '';
    };
    element.style.transition = `transform ${GLIDE_MS}ms`;
    element.style.transform = 'none';
    activeGlides.set(element, cleanup);
    element.addEventListener('transitionend', onEnd);
  }
}

/**
 * FLIP glide for replay: when `frameKey` changes, chips (`[data-glide-key]`
 * descendants of `container`) present once in both frames slide from their old
 * position to the new one. No-op when `frameKey` is null (live) or the user
 * prefers reduced motion.
 */
export function useChipGlide(container: React.RefObject<HTMLElement | null>, frameKey: string | null): void {
  const previous = useRef<IGlidePositions>(new Map());

  useLayoutEffect(() => {
    const root = container.current;
    if (frameKey === null || !root) {
      previous.current = new Map();
      return;
    }
    const { positions, visible, elements } = readPositions(root);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      const glides: IGlide[] = [];
      elements.forEach((element, key) => {
        const before = visible.get(key) ?? previous.current.get(key);
        const after = positions.get(key);
        if (!before || !after) return;
        const dx = before.left - after.left;
        const dy = before.top - after.top;
        if (dx !== 0 || dy !== 0) glides.push({ element, dx, dy });
      });
      playGlides(glides);
    }
    previous.current = positions;
  }, [container, frameKey]);
}
